import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { ChartCard, EmptyChart } from '../charts/ChartPrimitives';
import { CHART, formatPercent } from '../../lib/reporting-format';

export type AbcRow = {
  sku: string;
  units: number;
  throughputValue: number;
  share: number;
  cumulativeShare: number;
  abcClass: 'A' | 'B' | 'C';
};

interface AbcParetoCardProps {
  loading: boolean;
  rows: AbcRow[];
}

// Class fills: A leads with the brand accent, B/C step down to neutral slate.
const CLASS_FILL: Record<AbcRow['abcClass'], string> = {
  A: CHART.purple,
  B: CHART.slateDark,
  C: CHART.slate,
};

/**
 * ABC / Pareto: throughput-value bars sorted descending + a cumulative-share
 * line on a secondary axis (the canonical Pareto via recharts ComposedChart).
 * Capped at the top 40 SKUs so the x-axis stays legible; the cumulative line
 * still reflects the full distribution because share is computed server-side.
 */
export const AbcParetoCard: React.FC<AbcParetoCardProps> = ({ loading, rows }) => {
  const { t } = useTranslation('inventory');
  const { format, currency } = useCurrencyFormatter();

  const chartData = React.useMemo(
    () =>
      [...rows]
        .sort((a, b) => b.throughputValue - a.throughputValue)
        .slice(0, 40)
        .map((r) => ({
          sku: r.sku,
          value: r.throughputValue,
          cumulative: Math.round(r.cumulativeShare * 1000) / 10,
          abcClass: r.abcClass,
        })),
    [rows],
  );

  const counts = React.useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc[r.abcClass] += 1;
        return acc;
      },
      { A: 0, B: 0, C: 0 } as Record<AbcRow['abcClass'], number>,
    );
  }, [rows]);

  return (
    <ChartCard
      title={t('reporting.v2.abc', 'ABC analysis')}
      description={t(
        'reporting.v2.abcDesc',
        'SKUs ranked by throughput value. The line is cumulative share (Pareto).',
      )}
      action={
        rows.length > 0 ? (
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: CLASS_FILL.A }}
              />
              {t('reporting.v2.classCount', 'A: {{n}}', { n: counts.A })}
            </span>
            <span className="inline-flex items-center gap-1">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: CLASS_FILL.B }}
              />
              {t('reporting.v2.classCountB', 'B: {{n}}', { n: counts.B })}
            </span>
            <span className="inline-flex items-center gap-1">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: CLASS_FILL.C }}
              />
              {t('reporting.v2.classCountC', 'C: {{n}}', { n: counts.C })}
            </span>
          </div>
        ) : undefined
      }
    >
      {loading ? (
        <div className="h-96 animate-pulse rounded bg-muted/40" />
      ) : chartData.length === 0 ? (
        <EmptyChart
          message={t(
            'reporting.v2.noAbc',
            'No throughput in this period to classify.',
          )}
          className="h-96"
        />
      ) : (
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 40, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
              <XAxis
                dataKey="sku"
                tick={{ fontSize: 10, fill: CHART.axis }}
                tickLine={false}
                axisLine={{ stroke: CHART.grid }}
                angle={-45}
                textAnchor="end"
                interval={0}
                height={50}
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11, fill: CHART.axis }}
                tickLine={false}
                axisLine={false}
                width={48}
                tickFormatter={(v: number) => format(v, currency)}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 100]}
                tick={{ fontSize: 11, fill: CHART.axis }}
                tickLine={false}
                axisLine={false}
                width={40}
                tickFormatter={(v: number) => `${v}%`}
              />
              <Tooltip
                formatter={(value: number, name: string) => {
                  if (name === 'cumulative') {
                    return [`${formatPercent(value / 100)}`, t('reporting.v2.cumulative', 'Cumulative')];
                  }
                  return [format(value, currency), t('reporting.v2.throughput', 'Throughput')];
                }}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
              <Bar yAxisId="left" dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={24}>
                {chartData.map((entry) => (
                  <Cell key={entry.sku} fill={CLASS_FILL[entry.abcClass]} />
                ))}
              </Bar>
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="cumulative"
                stroke={CHART.amber}
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
};
